import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Card } from 'react-bootstrap';
import { Item, Action, ActionStatus } from './QuestionComponent';
import { QuestaoResponse, questionService, QuestaoRequest } from '../../../services/questionService';
import { itemService, CriarItemRequest } from '../../../services/itemService';
import { actionService, CriarAcaoRequest } from '../../../services/actionService';
import { userService, UsuarioSimplificadoResponse } from '../../../services/userService';
import { useEntity } from '../../../contexts/EntityContext';
import { useArea } from '../../../contexts/AreaContext';
import { useYear } from '../../../contexts/YearContext';

export type RecordType = 'question' | 'item' | 'action';

interface CreateNewRecordModalProps {
    show: boolean;
    onHide: () => void;
    recordType: RecordType;
    parentQuestion?: QuestaoResponse;
    parentItem?: Item;
    onSave: (record: QuestaoResponse | Item | Action) => void;
}

interface FormData {
    title: string;
    description: string;
    responsible: string;
    responsibleId: string; // Store the user ID
    expirationDate: string;
}

const CreateNewRecordModal: React.FC<CreateNewRecordModalProps> = ({
    show,
    onHide,
    recordType,
    parentQuestion,
    parentItem,
    onSave
}) => {
    const { currentEntity } = useEntity();
    const { currentArea } = useArea();
    const { currentYear } = useYear();
    const [formData, setFormData] = useState<FormData>({
        title: '',
        description: '',
        responsible: '',
        responsibleId: '',
        expirationDate: ''
    });

    const [errors, setErrors] = useState<{ [K in keyof FormData]?: string }>({});
    const [users, setUsers] = useState<UsuarioSimplificadoResponse[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // Reset form when modal opens/closes or record type changes
    useEffect(() => {
        if (show) {
            setFormData({
                title: '',
                description: '',
                responsible: '',
                responsibleId: '',
                expirationDate: ''
            });
            setErrors({});

            // Fetch users when modal opens
            if (currentEntity) {
                fetchUsers();
            }
        }
    }, [show, recordType, currentEntity]);

    const fetchUsers = async () => {
        if (!currentEntity) return;

        try {
            setUsersLoading(true);
            const usersData = await userService.getActiveUsersByEntity(currentEntity.entidade_id);
            setUsers(usersData);
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
        } finally {
            setUsersLoading(false);
        }
    };

    const getModalTitle = () => {
        switch (recordType) {
            case 'question':
                return 'Adicionar Nova Questão';
            case 'item':
                return 'Adicionar Novo Item';
            case 'action':
                return 'Adicionar Nova Ação';
            default:
                return 'Adicionar Novo Registro';
        }
    };

    const getParentInfo = () => {
        if (recordType === 'item' && parentQuestion) {
            return `Questão: ${parentQuestion.titulo}`;
        }
        if (recordType === 'action' && parentItem && parentQuestion) {
            return `Questão: ${parentQuestion.titulo} > Item: ${parentItem.titulo}`;
        }
        return null;
    };

    const validateForm = (): boolean => {
        const newErrors: { [K in keyof FormData]?: string } = {};

        // Title is always required
        if (!formData.title.trim()) {
            newErrors.title = 'Título é obrigatório';
        }

        // Description is required for all record types
        if (!formData.description.trim()) {
            newErrors.description = 'Descrição é obrigatória';
        }

        // Responsible is required for all record types
        if (!formData.responsible.trim()) {
            newErrors.responsible = 'Responsável é obrigatório';
        }

        // Expiration date validation for actions
        if (recordType === 'action' && formData.expirationDate) {
            if (!validateDate(formData.expirationDate)) {
                newErrors.expirationDate = 'Data inválida. Use o formato dd/mm/aaaa';
            } else {
                const expirationDate = parseDate(formData.expirationDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                if (expirationDate && expirationDate < today) {
                    newErrors.expirationDate = 'Data de expiração não pode ser no passado';
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        let newRecord: QuestaoResponse | Item | Action;

        switch (recordType) {
            case 'question':
                try {
                    if (!currentEntity || !currentArea || !currentYear) {
                        alert('Entidade, área ou ano não selecionados');
                        return;
                    }

                    const request: QuestaoRequest = {
                        titulo: formData.title,
                        descricao: formData.description,
                        ano: currentYear.ano,
                        responsavelId: formData.responsibleId
                    };

                    const createdQuestion = await questionService.createQuestion(
                        currentEntity.entidade_id,
                        currentArea.area_id,
                        request
                    );
                    newRecord = createdQuestion;
                } catch (error) {
                    console.error('Error creating question:', error);
                    alert('Erro ao criar questão. Por favor, tente novamente.');
                    return;
                }
                break;

            case 'item':
                if (!parentQuestion) return;
                try {
                    const request: CriarItemRequest = {
                        titulo: formData.title,
                        descricao: formData.description,
                        responsavelId: formData.responsibleId
                    };
                    const createdItem = await itemService.createItem(parentQuestion.questaoId, request);
                    newRecord = {
                        item_id: createdItem.item_id,
                        titulo: createdItem.titulo,
                        descricao: createdItem.descricao,
                        item_base_id: createdItem.item_base_id,
                        actions: [],
                        statusType: createdItem.statusType,
                        score: createdItem.score,
                        total_acoes: createdItem.total_acoes
                    } as Item;
                } catch (error) {
                    console.error('Error creating item:', error);
                    alert('Erro ao criar item. Por favor, tente novamente.');
                    return;
                }
                break;

            case 'action':
                if (!parentItem) return;
                try {
                    const request: CriarAcaoRequest = {
                        titulo: formData.title,
                        descricao: formData.description,
                        responsavelId: formData.responsibleId,
                        dataExpiracao: formData.expirationDate ? parseDate(formData.expirationDate)?.toISOString().split('T')[0] : undefined
                    };
                    const createdAction = await actionService.createAction(parentItem.item_id, request);
                    newRecord = {
                        ...createdAction,
                        status: createdAction.status as ActionStatus,
                        responses: []
                    } as Action;
                } catch (error) {
                    console.error('Error creating action:', error);
                    alert('Erro ao criar ação. Por favor, tente novamente.');
                    return;
                }
                break;

            default:
                return;
        }

        onSave(newRecord);
        onHide();
    };

    const handleInputChange = (field: keyof FormData, value: any) => {
        // Special handling for expiration date formatting
        if (field === 'expirationDate') {
            // Remove all non-numeric characters
            const numericValue = value.replace(/\D/g, '');

            // Format as dd/mm/yyyy
            let formattedValue = '';
            if (numericValue.length <= 2) {
                formattedValue = numericValue;
            } else if (numericValue.length <= 4) {
                formattedValue = `${numericValue.slice(0, 2)}/${numericValue.slice(2)}`;
            } else if (numericValue.length <= 8) {
                formattedValue = `${numericValue.slice(0, 2)}/${numericValue.slice(2, 4)}/${numericValue.slice(4, 8)}`;
            } else {
                formattedValue = `${numericValue.slice(0, 2)}/${numericValue.slice(2, 4)}/${numericValue.slice(4, 8)}`;
            }

            setFormData(prev => ({ ...prev, [field]: formattedValue }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined as any }));
        }
    };

    const validateDate = (dateString: string): boolean => {
        if (!dateString) return true; // Empty date is optional

        const parts = dateString.split('/');
        if (parts.length !== 3) return false;

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        if (isNaN(day) || isNaN(month) || isNaN(year)) return false;
        if (day < 1 || day > 31) return false;
        if (month < 1 || month > 12) return false;
        if (year < 1900 || year > 2100) return false;

        const date = new Date(year, month - 1, day);
        return date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year;
    };

    const parseDate = (dateString: string): Date | null => {
        if (!dateString) return null;

        const parts = dateString.split('/');
        if (parts.length !== 3) return null;

        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        return new Date(year, month - 1, day);
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
        >
            <Modal.Header>
                <Modal.Title>{getModalTitle()}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    {/* Parent Information */}
                    {getParentInfo() && (
                        <Card className="mb-4 bg-light">
                            <Card.Body className="py-2">
                                <small className="text-muted">
                                    <i className="mdi mdi-information-outline me-1"></i>
                                    {getParentInfo()}
                                </small>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Title Field - Always Required */}
                    <Form.Group className="mb-3">
                        <Form.Label>
                            Título {recordType === 'question' ? 'da Questão' : recordType === 'item' ? 'do Item' : 'da Ação'} *
                        </Form.Label>
                        <Form.Control
                            type="text"
                            value={formData.title}
                            onChange={(e) => handleInputChange('title', e.target.value)}
                            placeholder={`Digite o título ${recordType === 'question' ? 'da questão' : recordType === 'item' ? 'do item' : 'da ação'}`}
                            isInvalid={!!errors.title}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.title}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {/* Description Field - Required for all record types */}
                    <Form.Group className="mb-3">
                        <Form.Label>Descrição *</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder={`Digite a descrição ${recordType === 'question' ? 'da questão' : recordType === 'item' ? 'do item' : 'da ação'}`}
                            isInvalid={!!errors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.description}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {/* Responsible Field - Required for all record types */}
                    <Form.Group className="mb-3">
                        <Form.Label>Responsável *</Form.Label>
                        <Form.Control
                            as="select"
                            value={formData.responsibleId}
                            onChange={(e) => {
                                const selectedUser = users.find(user => user.usuarioId === e.target.value);
                                handleInputChange('responsibleId', e.target.value);
                                handleInputChange('responsible', selectedUser ? selectedUser.nome : '');
                            }}
                            isInvalid={!!errors.responsible}
                            disabled={usersLoading}
                        >
                            <option value="">Selecione um responsável...</option>
                            {users.map(user => (
                                <option key={user.usuarioId} value={user.usuarioId}>
                                    {user.nome} ({user.email})
                                </option>
                            ))}
                        </Form.Control>
                        {usersLoading && (
                            <Form.Text className="text-muted">
                                <small>Carregando usuários...</small>
                            </Form.Text>
                        )}
                        <Form.Control.Feedback type="invalid">
                            {errors.responsible}
                        </Form.Control.Feedback>
                    </Form.Group>

                    {/* Expiration Date Field - Only for actions
                    {recordType === 'action' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Data de Expiração</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.expirationDate}
                                onChange={(e) => handleInputChange('expirationDate', e.target.value)}
                                placeholder="dd/mm/aaaa"
                                isInvalid={!!errors.expirationDate}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.expirationDate}
                            </Form.Control.Feedback>
                        </Form.Group>
                    )} */}
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Salvar
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateNewRecordModal;
